/**
 * Sintetizador Acústico Procedural con Web Audio API (0 KB de archivos externos)
 * para el Scanner Web PWA Multi-Estación de Xolo Fest 2026 (HU-04).
 *
 * Genera ondas matemáticas en tiempo real:
 * - SUCCESS_ARPEGGIO: Arpegio ascendente brillante (C5 -> E5 -> G5 -> C6)
 * - ERROR_BUZZER: Buzzer grave disonante descendente (180 Hz -> 90 Hz, sawtooth, 380 ms)
 * - WARNING_DOUBLE_PULSE: Doble pulso de alerta (440 Hz, 120ms ON -> 80ms pausa -> 160ms ON, square)
 */

let sharedAudioCtx = null;

/**
 * Obtiene o inicializa la instancia compartida de AudioContext y la reanuda si está suspendida.
 * @returns {AudioContext|null}
 */
export const getSharedAudioContext = () => {
    if (typeof window === 'undefined') return null;

    const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtxClass) return null;

    try {
        if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
            sharedAudioCtx = new AudioCtxClass();
        }
        if (sharedAudioCtx.state === 'suspended') {
            sharedAudioCtx.resume().catch(() => {});
        }
        return sharedAudioCtx;
    } catch (error) {
        console.warn('No fue posible inicializar Web Audio API:', error);
        return null;
    }
};

/**
 * Reproduce un arpegio ascendente brillante (C5 -> E5 -> G5 -> C6) para ACCESO CONCEDIDO (Verde).
 * Frecuencias: 523.25 Hz -> 659.25 Hz -> 783.99 Hz -> 1046.50 Hz.
 */
export const playAccessGrantedTone = () => {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    try {
        const now = ctx.currentTime;
        const notes = [
            { freq: 523.25, start: 0.0, duration: 0.09, type: 'triangle' },  // C5
            { freq: 659.25, start: 0.075, duration: 0.09, type: 'triangle' }, // E5
            { freq: 783.99, start: 0.15, duration: 0.09, type: 'triangle' },  // G5
            { freq: 1046.5, start: 0.225, duration: 0.22, type: 'sine' }      // C6
        ];

        notes.forEach(({ freq, start, duration, type }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = type;
            osc.frequency.setValueAtTime(freq, now + start);

            gain.gain.setValueAtTime(0.0001, now + start);
            gain.gain.linearRampToValueAtTime(0.32, now + start + 0.015);
            gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(now + start);
            osc.stop(now + start + duration + 0.02);
        });
    } catch (error) {
        console.warn('Error al reproducir playAccessGrantedTone:', error);
    }
};

/**
 * Reproduce un buzzer grave disonante descendente (180 Hz -> 90 Hz, 380 ms, onda sawtooth)
 * para ACCESO DENEGADO por zona restringida, pre-registro obligatorio o boleto inválido (Rojo).
 */
export const playAccessDeniedTone = () => {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    try {
        const now = ctx.currentTime;
        const duration = 0.38; // 380 ms

        const oscMain = ctx.createOscillator();
        const oscSub = ctx.createOscillator();
        const gain = ctx.createGain();

        oscMain.type = 'sawtooth';
        oscMain.frequency.setValueAtTime(180, now);
        oscMain.frequency.linearRampToValueAtTime(90, now + duration);

        // Segunda voz ligeramente desafinada para reforzar el carácter de buzzer industrial
        oscSub.type = 'sawtooth';
        oscSub.frequency.setValueAtTime(172, now);
        oscSub.frequency.linearRampToValueAtTime(84, now + duration);

        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.linearRampToValueAtTime(0.36, now + 0.02);
        gain.gain.setValueAtTime(0.32, now + duration - 0.06);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

        oscMain.connect(gain);
        oscSub.connect(gain);
        gain.connect(ctx.destination);

        oscMain.start(now);
        oscSub.start(now);
        oscMain.stop(now + duration + 0.02);
        oscSub.stop(now + duration + 0.02);
    } catch (error) {
        console.warn('Error al reproducir playAccessDeniedTone:', error);
    }
};

/**
 * Reproduce un doble pulso de alerta a 440 Hz (120 ms ON -> 80 ms pausa -> 160 ms ON)
 * con onda square para advertencia amarilla de DOBLE ENTRADA / ANTI-PASSBACK.
 */
export const playAntiPassbackWarningTone = () => {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    try {
        const now = ctx.currentTime;
        const pulses = [
            { freq: 440, start: 0.0, duration: 0.12 },  // 120 ms ON
            // 80 ms pausa (0.12 -> 0.20)
            { freq: 440, start: 0.20, duration: 0.16 }  // 160 ms ON
        ];

        pulses.forEach(({ freq, start, duration }) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'square';
            osc.frequency.setValueAtTime(freq, now + start);

            gain.gain.setValueAtTime(0.0001, now + start);
            gain.gain.linearRampToValueAtTime(0.26, now + start + 0.01);
            gain.gain.setValueAtTime(0.24, now + start + duration - 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(now + start);
            osc.stop(now + start + duration + 0.015);
        });
    } catch (error) {
        console.warn('Error al reproducir playAntiPassbackWarningTone:', error);
    }
};

/**
 * Despacha automáticamente el tono sintético según el audioCue devuelto por el backend.
 * @param {'SUCCESS_ARPEGGIO'|'ERROR_BUZZER'|'WARNING_DOUBLE_PULSE'|string} audioCue
 */
export const playScannerBeepByCue = (audioCue) => {
    const normalized = String(audioCue || '').trim().toUpperCase();

    if (normalized === 'SUCCESS_ARPEGGIO' || normalized === 'GRANTED' || normalized === 'GREEN') {
        playAccessGrantedTone();
        return;
    }

    if (
        normalized === 'WARNING_DOUBLE_PULSE' ||
        normalized === 'DENIED_ANTI_PASSBACK' ||
        normalized === 'YELLOW'
    ) {
        playAntiPassbackWarningTone();
        return;
    }

    if (
        normalized === 'ERROR_BUZZER' ||
        normalized === 'DENIED_CATEGORY' ||
        normalized === 'DENIED_PRE_REGISTRATION_REQUIRED' ||
        normalized === 'INVALID_TICKET' ||
        normalized === 'RED'
    ) {
        playAccessDeniedTone();
        return;
    }

    // Fallback por defecto
    playAccessDeniedTone();
};
